import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Check, Clock, X, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export default function PortalGroups() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/portal/groups");
      setGroups(data);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleJoin = async (g) => {
    setBusyId(g.id);
    try {
      await api.post(`/portal/groups/${g.id}/join`);
      toast.success(`Request sent. A leader will approve you soon.`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not request to join");
    } finally { setBusyId(null); }
  };

  const handleLeave = async (g) => {
    if (!window.confirm(`Leave ${g.name}?`)) return;
    setBusyId(g.id);
    try {
      await api.delete(`/portal/groups/${g.id}/leave`);
      toast.success("You've left the group");
      load();
    } catch (err) {
      toast.error("Could not leave");
    } finally { setBusyId(null); }
  };

  const filtered = groups.filter((g) =>
    !query.trim() || g.name.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div data-testid="portal-groups">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-3">Find Your People</div>
        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">Groups & <span className="italic text-electric">Auxiliaries.</span></h1>
      </div>

      <div className="relative mb-8 max-w-md">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" aria-hidden="true" />
        <label htmlFor="groups-search" className="sr-only">Search groups</label>
        <input
          id="groups-search"
          type="search"
          placeholder="Search groups..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full pl-11 pr-4 py-3 rounded-full bg-[#0a0a0a] border border-white/10 text-white placeholder-zinc-500 focus:outline-none focus:border-electric focus-visible:ring-2 focus-visible:ring-electric/40"
          data-testid="groups-search"
        />
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><div className="w-8 h-8 border-2 border-electric border-t-transparent rounded-full animate-spin" role="status" aria-label="Loading groups" /></div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((g, i) => (
            <motion.div
              key={g.id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: (i % 9) * 0.05 }}
              className="rounded-2xl border border-white/5 bg-[#0a0a0a] p-6 flex flex-col"
              data-testid={`group-card-${g.slug}`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-3xl" aria-hidden="true">{g.icon || "✨"}</span>
                {g.my_status === "approved" && (
                  <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-electric/10 text-electric border border-electric/20 font-bold inline-flex items-center gap-1">
                    <Check className="w-3 h-3" /> Member
                  </span>
                )}
                {g.my_status === "pending" && (
                  <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 font-bold inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Pending
                  </span>
                )}
                {g.my_status === "declined" && (
                  <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/20 font-bold inline-flex items-center gap-1">
                    <X className="w-3 h-3" /> Declined
                  </span>
                )}
              </div>
              <div className="font-display font-bold text-lg mb-1">{g.name}</div>
              <div className="text-sm text-zinc-400 leading-relaxed flex-1 mb-4">{g.description}</div>
              <div className="text-xs text-zinc-500 mb-4">{g.member_count} {g.member_count === 1 ? "member" : "members"}</div>

              {g.my_status === "approved" ? (
                <div className="flex gap-2">
                  <Link to={`/portal/groups/${g.slug}`} className="flex-1 inline-flex items-center justify-center gap-2 bg-electric text-black px-4 py-2.5 rounded-full font-bold text-xs hover:bg-[#4ade80] transition" data-testid={`group-open-${g.slug}`}>
                    Open
                  </Link>
                  <button onClick={() => handleLeave(g)} disabled={busyId === g.id} className="px-4 py-2.5 rounded-full text-xs font-bold border border-white/10 text-zinc-400 hover:text-red-400 hover:border-red-400/30 transition disabled:opacity-60" data-testid={`group-leave-${g.slug}`}>
                    Leave
                  </button>
                </div>
              ) : g.my_status === "pending" ? (
                <button disabled className="w-full inline-flex items-center justify-center gap-2 bg-white/5 text-zinc-500 px-4 py-2.5 rounded-full font-bold text-xs cursor-not-allowed" data-testid={`group-pending-${g.slug}`}>
                  <Clock className="w-3.5 h-3.5" /> Awaiting approval
                </button>
              ) : (
                <button
                  onClick={() => handleJoin(g)}
                  disabled={busyId === g.id}
                  className="w-full inline-flex items-center justify-center gap-2 bg-electric text-black px-4 py-2.5 rounded-full font-bold text-xs hover:bg-[#4ade80] transition disabled:opacity-60"
                  data-testid={`group-join-${g.slug}`}
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  {busyId === g.id ? "Sending..." : "Request to Join"}
                </button>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
